import { Button, CloseIcon } from '@repo/atomic-components';
import { SearchTextControl, SearchTextControlHandle } from './SearchTextControl';
import { useCallback, useMemo, useRef } from 'react';
import {
  resetSelectedCasesFilters,
  selectCasesFilters,
  selectSelectedCasesFilters,
  selectUserAboutData,
  setSelectedCasesFilters,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { useAccessControl } from '@/hooks';
import { AccessControlAction, CASE_STATUS_RAW } from '@/constants';
import { CasesFiltersUrgentOptions, DEFAULT_HIDDEN_STATUS_LABELS } from '@/api';
import { classNames } from 'primereact/utils';

interface ActionsPanel {
  handleCreateNewCase: () => void;
}

export const ActionsPanel = ({ handleCreateNewCase }: ActionsPanel) => {
  const dispatch = useAppDispatch();
  // Ref for SearchTextControl imperative handle — quick-filter clicks and
  // "Clear all" both reset the search box so the visible filter state is
  // consistent.
  const searchTextControlRef = useRef<SearchTextControlHandle | null>(null);

  const isCreateEditCase = useAccessControl(AccessControlAction.CREATE_EDIT_CASE);

  const casesFilters = useAppSelector(selectCasesFilters);
  const selectedCasesFilters = useAppSelector(selectSelectedCasesFilters);
  const userAboutData = useAppSelector(selectUserAboutData);

  // The page's "at rest" status filter — every status that isn't hidden by
  // default (see DEFAULT_HIDDEN_STATUS_LABELS in getCasesFilters). This is
  // both what the page applies on initial load and what every quick filter
  // preserves, so the hidden-by-default statuses stay hidden whether the
  // user is on a quick filter or no filter at all.
  const defaultVisibleStatuses = useMemo(
    () => casesFilters.status.filter((s) => !DEFAULT_HIDDEN_STATUS_LABELS.has(s.rendered)),
    [casesFilters.status],
  );

  // True when the status slice is in its "default" shape — either empty
  // (after a hard "Clear all") or exactly the default-visible set. Used to
  // treat the default as "no filter applied" rather than "a status filter is on".
  const isStatusAtRest = useMemo(() => {
    const statuses = selectedCasesFilters.status;
    if (statuses.length === 0) return true;
    if (statuses.length !== defaultVisibleStatuses.length) return false;
    const allowed = new Set(defaultVisibleStatuses.map((s) => s.raw));
    return statuses.every((s) => allowed.has(s.raw));
  }, [defaultVisibleStatuses, selectedCasesFilters.status]);

  // A quick-filter chip lights up when the Redux filter slice EXACTLY matches
  // what that chip would set — i.e. the chip is the sole active filter
  // alongside the always-on non-closed status default.
  const isUrgentActive = useMemo(
    () =>
      isStatusAtRest &&
      selectedCasesFilters.isUrgent.length === 1 &&
      selectedCasesFilters.isUrgent[0] === CasesFiltersUrgentOptions.YES &&
      selectedCasesFilters.assignedTo.length === 0 &&
      selectedCasesFilters.createdBy.length === 0,
    [isStatusAtRest, selectedCasesFilters],
  );

  const isAssignedToMeActive = useMemo(
    () =>
      isStatusAtRest &&
      userAboutData != null &&
      selectedCasesFilters.assignedTo.length === 1 &&
      selectedCasesFilters.assignedTo[0]?.userId === userAboutData.userId &&
      selectedCasesFilters.isUrgent.length === 0 &&
      selectedCasesFilters.createdBy.length === 0,
    [isStatusAtRest, selectedCasesFilters, userAboutData],
  );

  // "Ready to assign" surfaces the queue of cases that have finished AI
  // review and need a human owner picked — status is Ready For Review and no
  // one is assigned yet. Looked up dynamically from the filter endpoint's
  // response so we don't hard-code the API's rendered label alongside the
  // raw key.
  const readyForReviewStatus = useMemo(
    // `s.raw` is typed as the value union (misnamed `CaseStatusOptionsKeys`),
    // but at runtime the backend returns the uppercase snake_case key — see
    // DEFAULT_CASE_STATUSES in useCasesListTable, which is compared against
    // s.raw's real runtime shape. Cast so this comparison type-checks.
    () => casesFilters.status.find((s) => (s.raw as string) === CASE_STATUS_RAW.READY_FOR_REVIEW),
    [casesFilters.status],
  );

  // Whether the assignedTo filter has "Unassigned" (represented as `null`)
  // selected — the assignable list stores unassigned as a `null` entry, so
  // that's what a match looks like in the selected filter slice.
  const isReadyToAssignActive = useMemo(
    () =>
      readyForReviewStatus != null &&
      selectedCasesFilters.status.length === 1 &&
      (selectedCasesFilters.status[0].raw as string) === CASE_STATUS_RAW.READY_FOR_REVIEW &&
      selectedCasesFilters.assignedTo.length === 1 &&
      selectedCasesFilters.assignedTo[0] === null &&
      selectedCasesFilters.createdBy.length === 0 &&
      selectedCasesFilters.isUrgent.length === 0,
    [readyForReviewStatus, selectedCasesFilters],
  );

  // "Clear filters" is visible whenever ANY filter slice has a value — column-
  // header filters, quick filters, the on-load non-closed status default, all
  // count. The only state in which it hides is "every slice empty", which is
  // the post-clear state. Search term is NOT included here since "Clear
  // filters" only clears filters, not the search.
  const anyFilterActive = useMemo(
    () =>
      selectedCasesFilters.status.length > 0 ||
      selectedCasesFilters.assignedTo.length > 0 ||
      selectedCasesFilters.createdBy.length > 0 ||
      selectedCasesFilters.isUrgent.length > 0,
    [selectedCasesFilters],
  );

  const clearAll = useCallback(() => {
    dispatch(resetSelectedCasesFilters());
    // Don't reset the search term — "Clear filters" only clears filters
  }, [dispatch]);

  const applyUrgent = useCallback(() => {
    dispatch(
      setSelectedCasesFilters({
        assignedTo: [],
        createdBy: [],
        status: defaultVisibleStatuses,
        isUrgent: [CasesFiltersUrgentOptions.YES],
      }),
    );
    searchTextControlRef.current?.reset?.();
  }, [dispatch, defaultVisibleStatuses]);

  const applyAssignedToMe = useCallback(() => {
    if (!userAboutData) return;
    const me = casesFilters.assignedTo.find((u) => u?.userId === userAboutData.userId);
    if (!me) return; // current user isn't in the assignable list (defensive)
    dispatch(
      setSelectedCasesFilters({
        assignedTo: [me],
        createdBy: [],
        status: defaultVisibleStatuses,
        isUrgent: [],
      }),
    );
    searchTextControlRef.current?.reset?.();
  }, [casesFilters.assignedTo, dispatch, defaultVisibleStatuses, userAboutData]);

  const applyReadyToAssign = useCallback(() => {
    if (!readyForReviewStatus) return; // filters endpoint hasn't responded yet
    dispatch(
      setSelectedCasesFilters({
        assignedTo: [null],
        createdBy: [],
        status: [readyForReviewStatus],
        isUrgent: [],
      }),
    );
    searchTextControlRef.current?.reset?.();
  }, [dispatch, readyForReviewStatus]);

  // Quick-filter chip — inactive = outlined grey pill, active = pale-mint
  // bg with dark-green border + text. `border` is always applied (even in
  // the active state) so toggling between active/inactive doesn't shift the
  // chip's box size by the border width. Clicking the active chip clears
  // all filters (toggle-off behaviour).
  const chipClass = (active: boolean) =>
    classNames(
      'flex h-8 items-center rounded-full border px-3 text-sm whitespace-nowrap transition-colors cursor-pointer',
      active
        ? 'border-fletchers-green-600 bg-fletchers-green-50 text-fletchers-green-600'
        : 'border-fletchers-gray-110 text-fletchers-black-900 hover:bg-fletchers-gray-50',
    );

  return (
    <div className="bg-fletchers-gray-25 flex h-full w-full flex-row items-center gap-2 px-6 py-4">
      <span className="mr-auto text-[20px] font-semibold whitespace-nowrap">Case List</span>

      {/* Quick-filters group — one-click preset filter combos. Only one can
          be active at a time; clicking a new chip replaces whatever filter
          state is current. The active chip can be clicked again to clear. */}
      <div className="flex items-center gap-2">
        <span className="text-fletchers-gray-200 text-sm whitespace-nowrap">Quick filters:</span>

        <button type="button" onClick={isUrgentActive ? clearAll : applyUrgent} className={chipClass(isUrgentActive)}>
          Urgent
        </button>

        <button
          type="button"
          onClick={isAssignedToMeActive ? clearAll : applyAssignedToMe}
          className={chipClass(isAssignedToMeActive)}
        >
          Assigned to me
        </button>

        {/* Only offered once the filter endpoint has responded — without the
            Ready For Review option in `casesFilters.status`, the chip has
            nothing to apply. */}
        {readyForReviewStatus && (
          <button
            type="button"
            onClick={isReadyToAssignActive ? clearAll : applyReadyToAssign}
            className={chipClass(isReadyToAssignActive)}
          >
            Ready to assign
          </button>
        )}

        {anyFilterActive && (
          <>
            {/* 32px vertical divider separates the always-visible quick-
                filter chips from the conditionally-visible Clear filters
                button. Matches the right-hand divider before the search. */}
            <div className="bg-fletchers-gray-110 mx-2 h-[30px] w-px" />
            <button
              type="button"
              onClick={clearAll}
              className="text-fletchers-gray-200 hover:text-fletchers-black-900 flex h-8 cursor-pointer items-center gap-1.5 rounded-full px-0 text-sm font-semibold whitespace-nowrap"
            >
              <CloseIcon className="max-h-[10px] max-w-[10px]" />
              Clear filters
            </button>
          </>
        )}
      </div>

      {/* Vertical divider between the quick-filters group and the search bar */}
      <div className="bg-fletchers-gray-110 mx-2 h-[30px] w-px" />

      <div className="w-4xs">
        <SearchTextControl ref={searchTextControlRef} />
      </div>

      {isCreateEditCase && (
        // Inline `style` overrides PrimeReact Button's default vertical
        // padding/height so the button reliably renders at exactly 32px
        // tall (matching the quick-filter chips) with 16px left/right
        // padding around the icon + label. Icon size set to 12×12px.
        <Button
          className="whitespace-nowrap [&_.pi]:text-[12px] [&>span]:font-semibold"
          label="Create case"
          icon="pi pi-plus"
          onClick={handleCreateNewCase}
          style={{ height: 32, paddingTop: 0, paddingBottom: 0, paddingLeft: 16, paddingRight: 16 }}
        />
      )}
    </div>
  );
};
